import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Edit, Calendar, Users, Sparkles, ChevronDown, ChevronUp, FileText, Star, Footprints, Dumbbell, CheckCircle, Check, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPO_COLORS = {
  fuerza: { bg: 'bg-amber-900/40', text: 'text-amber-400', label: 'FUE', dot: 'bg-amber-400' },
  carrera: { bg: 'bg-blue-900/40', text: 'text-blue-400', label: 'CAR', dot: 'bg-blue-400' },
  bike: { bg: 'bg-orange-900/40', text: 'text-orange-400', label: 'BIK', dot: 'bg-orange-400' },
  funcional: { bg: 'bg-yellow-900/40', text: 'text-yellow-400', label: 'FUN', dot: 'bg-yellow-400' },
  excursion: { bg: 'bg-green-900/40', text: 'text-green-400', label: 'EXC', dot: 'bg-green-400' },
  natacion: { bg: 'bg-cyan-900/40', text: 'text-cyan-400', label: 'NAT', dot: 'bg-cyan-400' },
  especial: { bg: 'bg-pink-900/40', text: 'text-pink-400', label: 'ESP', dot: 'bg-pink-400' },
  descanso: { bg: 'bg-gray-800/40', text: 'text-gray-500', label: 'DES', dot: 'bg-gray-500' },
  evento: { bg: 'bg-gold/20', text: 'text-gold', label: 'EVT', dot: 'bg-gold' },
};

const DIAS = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];

export default function PlanesEntrenamiento() {
  const [planes, setPlanes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [asignables, setAsignables] = useState([]);
  const [planStats, setPlanStats] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showParseDialog, setShowParseDialog] = useState(false);
  const [parseText, setParseText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]); // ids destinatarios del plan
  const [memberSearch, setMemberSearch] = useState('');

  // Form state
  const [form, setForm] = useState({
    nombre: '', descripcion: '', fecha_inicio: '', num_semanas: 1,
    cliente_id: '', evento: { nombre: '', fecha_inicio: '', fecha_fin: '' },
    recomendaciones: [], semanas: []
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadPlanes = useCallback(async () => {
    try {
      const [planesRes, statsRes] = await Promise.all([
        axios.get(`${API}/planes`, { headers }),
        axios.get(`${API}/planes/stats-all`, { headers })
      ]);
      setPlanes(planesRes.data);
      setPlanStats(statsRes.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadClientes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/client-plus/users`, { headers });
      setClientes(data);
    } catch (e) { console.error(e); }
  }, []);

  const loadAsignables = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/planes/asignables`, { headers });
      setAsignables(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadPlanes(); loadClientes(); loadAsignables(); }, [loadPlanes, loadClientes, loadAsignables]);

  const resetForm = () => {
    setForm({
      nombre: '', descripcion: '', fecha_inicio: '', num_semanas: 1,
      cliente_id: '', evento: { nombre: '', fecha_inicio: '', fecha_fin: '' },
      recomendaciones: [], semanas: []
    });
    setSelectedIds([]);
    setMemberSearch('');
  };

  const handleParse = async () => {
    if (!parseText.trim()) return;
    setParsing(true);
    try {
      const { data } = await axios.post(`${API}/planes/parse`, { texto: parseText }, { headers });
      if (data.error) {
        alert('Error al parsear: ' + data.error);
        return;
      }
      setForm(prev => ({
        ...prev,
        nombre: data.nombre || prev.nombre,
        descripcion: data.descripcion || prev.descripcion,
        fecha_inicio: data.fecha_inicio || prev.fecha_inicio,
        num_semanas: data.num_semanas || prev.num_semanas,
        evento: data.evento || prev.evento,
        recomendaciones: data.recomendaciones || prev.recomendaciones,
        semanas: data.semanas || prev.semanas,
      }));
      setShowParseDialog(false);
      setParseText('');
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setParsing(false); }
  };

  const handleSave = async () => {
    if (!form.nombre || !form.fecha_inicio) {
      alert('Nombre y fecha de inicio son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const evento = form.evento?.nombre ? form.evento : null;
      
      // MODO CREAR + multi-destinatarios: usar /planes/bulk
      if (!form.id && selectedIds.length > 0) {
        const payload = {
          nombre: form.nombre,
          descripcion: form.descripcion,
          fecha_inicio: form.fecha_inicio,
          num_semanas: form.num_semanas,
          cliente_ids: selectedIds,
          evento,
          recomendaciones: form.recomendaciones,
          semanas: form.semanas,
          feed_publico: true,
        };
        const { data } = await axios.post(`${API}/planes/bulk`, payload, { headers });
        alert(`✅ Plan asignado a ${data.creados} miembro(s): ${(data.destinatarios || []).join(', ')}`);
        setShowCreate(false);
        resetForm();
        loadPlanes();
        return;
      }
      
      // MODO CLASICO (editar o un solo destinatario antiguo)
      const payload = {
        ...form,
        cliente_id: form.cliente_id || undefined,
        evento,
      };
      if (form.id) {
        await axios.put(`${API}/planes/${form.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/planes`, payload, { headers });
      }
      setShowCreate(false);
      resetForm();
      loadPlanes();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este plan?')) return;
    try {
      await axios.delete(`${API}/planes/${id}`, { headers });
      loadPlanes();
      setShowDetail(null);
    } catch (e) { alert('Error al eliminar'); }
  };

  const openEdit = (plan) => {
    setForm({ ...plan });
    setShowDetail(null);
    setShowCreate(true);
  };

  const [expandedWeek, setExpandedWeek] = useState(null);

  return (
    <>
    <Navbar />
    <div className="min-h-screen bg-dark-mk p-4 pb-24" data-testid="planes-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Planes de Entrenamiento</h1>
          <p className="text-gray-400 text-sm mt-1">Crea planes semanales para tus clientes</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true); }}
          className="bg-gold hover:bg-gold/80 text-black font-medium"
          data-testid="btn-new-plan">
          <Plus className="h-4 w-4 mr-1" /> Nuevo
        </Button>
      </div>

      {/* Plans list - AGRUPADOS por nombre + fecha + duracion + entrenador */}
      {planes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <Calendar className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-sm">No hay planes creados</p>
          <p className="text-xs mt-1">Crea tu primer plan de entrenamiento</p>
        </div>
      ) : (
        <PlanesAgrupados
          planes={planes}
          planStats={planStats}
          asignables={asignables}
          onOpenDetail={setShowDetail}
          onReload={loadPlanes}
        />
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Quick parse button */}
            <Button type="button" variant="outline" className="w-full border-gold/50 text-gold hover:bg-gold/10"
              onClick={() => setShowParseDialog(true)} data-testid="btn-parse">
              <Sparkles className="h-4 w-4 mr-2" /> Importar con IA (pegar texto del plan)
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-gray-400 text-xs mb-1 block">NOMBRE DEL PLAN *</label>
                <Input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Plan Camino Santiago" className="bg-gray-800 border-gray-700 text-white" data-testid="input-nombre" />
              </div>
              <div className="col-span-2">
                <label className="text-gray-400 text-xs mb-1 block">DESCRIPCIÓN</label>
                <Input value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))}
                  placeholder="Descripción corta" className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">FECHA INICIO *</label>
                <Input type="date" value={form.fecha_inicio} onChange={e => setForm(p => ({ ...p, fecha_inicio: e.target.value }))}
                  className="bg-gray-800 border-gray-700 text-white" data-testid="input-fecha" />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">SEMANAS</label>
                <Input type="number" min={1} max={52} value={form.num_semanas}
                  onChange={e => setForm(p => ({ ...p, num_semanas: parseInt(e.target.value) || 1 }))}
                  className="bg-gray-800 border-gray-700 text-white" />
              </div>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-gray-400 text-xs">ASIGNAR A {form.id ? 'CLIENTE' : 'MIEMBROS'}</label>
                  {!form.id && (
                    <span className="text-[10px] text-gold/80">
                      {selectedIds.length > 0
                        ? `${selectedIds.length} seleccionado(s)`
                        : 'Si no eliges nadie → plan personal'}
                    </span>
                  )}
                </div>
                {form.id ? (
                  /* Modo edicion: select clasico */
                  <Select value={form.cliente_id} onValueChange={v => setForm(p => ({ ...p, cliente_id: v }))}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="select-cliente">
                      <SelectValue placeholder="Yo mismo (entrenador)" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="self" className="text-white">Yo mismo</SelectItem>
                      {clientes.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-white">{c.nombre} ({c.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  /* Modo creacion: multi-picker con buscador */
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 space-y-2" data-testid="multi-picker-miembros">
                    <div className="flex gap-2 items-center">
                      <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                        <Input
                          placeholder="Buscar miembro..."
                          value={memberSearch}
                          onChange={(e) => setMemberSearch(e.target.value)}
                          className="bg-gray-900 border-gray-700 text-white text-sm pl-7 h-8"
                          data-testid="input-search-miembros"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const filtered = asignables.filter(u =>
                            !memberSearch ||
                            (u.nombre || '').toLowerCase().includes(memberSearch.toLowerCase()) ||
                            (u.email || '').toLowerCase().includes(memberSearch.toLowerCase())
                          );
                          const ids = filtered.map(u => u.id);
                          const allSelected = ids.every(id => selectedIds.includes(id));
                          setSelectedIds(allSelected
                            ? selectedIds.filter(id => !ids.includes(id))
                            : Array.from(new Set([...selectedIds, ...ids])));
                        }}
                        className="text-[11px] px-2 py-1 rounded bg-gold/20 hover:bg-gold/30 text-gold font-semibold"
                        data-testid="btn-toggle-todos"
                      >
                        {memberSearch ? 'Todos los filtrados' : 'Todos'}
                      </button>
                      {selectedIds.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedIds([])}
                          className="text-[11px] px-2 py-1 rounded bg-red-900/30 hover:bg-red-900/50 text-red-300"
                          data-testid="btn-limpiar-seleccion"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>
                    
                    {/* Chips de seleccionados */}
                    {selectedIds.length > 0 && (
                      <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto p-1 bg-gray-900/50 rounded">
                        {selectedIds.map(id => {
                          const u = asignables.find(x => x.id === id);
                          if (!u) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1 bg-gold/20 text-gold text-[10px] px-2 py-0.5 rounded-full"
                            >
                              {u.nombre}
                              <button
                                type="button"
                                onClick={() => setSelectedIds(selectedIds.filter(s => s !== id))}
                                className="hover:text-white"
                              >×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Lista de miembros */}
                    <div className="max-h-44 overflow-y-auto space-y-0.5">
                      {asignables
                        .filter(u =>
                          !memberSearch ||
                          (u.nombre || '').toLowerCase().includes(memberSearch.toLowerCase()) ||
                          (u.email || '').toLowerCase().includes(memberSearch.toLowerCase())
                        )
                        .map(u => {
                          const sel = selectedIds.includes(u.id);
                          return (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => setSelectedIds(sel
                                ? selectedIds.filter(s => s !== u.id)
                                : [...selectedIds, u.id])}
                              data-testid={`miembro-pick-${u.id}`}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${sel ? 'bg-gold/20 text-gold' : 'text-gray-300 hover:bg-gray-700'}`}
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${sel ? 'bg-gold border-gold' : 'border-gray-600'}`}>
                                {sel && <Check className="h-3 w-3 text-gray-900" />}
                              </div>
                              <span className="flex-1 truncate">{u.nombre}</span>
                              {u.role === 'entrenador' && <span className="text-[9px] bg-gray-700 px-1 rounded">ENTRENADOR</span>}
                              {u.role === 'client_plus' && <span className="text-[9px] bg-blue-700 px-1 rounded">PLUS</span>}
                              {u.es_pro && <span className="text-[9px] bg-amber-600 text-black px-1 rounded">PRO</span>}
                            </button>
                          );
                        })}
                      {asignables.length === 0 && (
                        <p className="text-gray-500 text-xs text-center py-2">No hay miembros</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Evento especial */}
            <div className="border border-gold/30 rounded-lg p-3">
              <p className="text-gold text-xs font-bold mb-2"><Star className="h-3 w-3 inline mr-1" />EVENTO ESPECIAL (Opcional)</p>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Nombre" value={form.evento?.nombre || ''} className="bg-gray-800 border-gray-700 text-white text-xs col-span-3"
                  onChange={e => setForm(p => ({ ...p, evento: { ...p.evento, nombre: e.target.value } }))} />
                <Input type="date" value={form.evento?.fecha_inicio || ''} className="bg-gray-800 border-gray-700 text-white text-xs"
                  onChange={e => setForm(p => ({ ...p, evento: { ...p.evento, fecha_inicio: e.target.value } }))} />
                <Input type="date" value={form.evento?.fecha_fin || ''} className="bg-gray-800 border-gray-700 text-white text-xs"
                  onChange={e => setForm(p => ({ ...p, evento: { ...p.evento, fecha_fin: e.target.value } }))} />
              </div>
            </div>

            {/* Weeks preview */}
            {form.semanas?.length > 0 && (
              <div>
                <p className="text-gray-400 text-xs font-bold mb-2">SEMANAS ({form.semanas.length})</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {form.semanas.map((sem, si) => (
                    <div key={si} className="bg-gray-800 rounded-lg border border-gray-700">
                      <button onClick={() => setExpandedWeek(expandedWeek === si ? null : si)}
                        className="w-full flex items-center justify-between p-2 text-sm text-white">
                        <span className="font-medium">{sem.nombre || `Semana ${sem.numero}`}</span>
                        {expandedWeek === si ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {expandedWeek === si && (
                        <div className="px-2 pb-2 space-y-1">
                          {sem.dias?.map((dia, di) => (
                            <div key={di} className="flex items-start gap-2 text-xs">
                              <span className="text-gray-500 w-8 flex-shrink-0 font-mono">{DIAS[dia.dia]}</span>
                              <div className="flex flex-wrap gap-1">
                                {dia.actividades?.map((act, ai) => {
                                  const tc = TIPO_COLORS[act.tipo] || TIPO_COLORS.especial;
                                  return (
                                    <span key={ai} className={`${tc.bg} ${tc.text} px-1.5 py-0.5 rounded text-[10px]`}>
                                      {tc.label} {act.descripcion}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recomendaciones */}
            {form.recomendaciones?.length > 0 && (
              <div>
                <p className="text-gray-400 text-xs font-bold mb-2">RECOMENDACIONES ({form.recomendaciones.length})</p>
                <div className="space-y-1">
                  {form.recomendaciones.map((r, i) => (
                    <div key={i} className="bg-gray-800 rounded px-2 py-1 text-xs">
                      <span className="text-gold font-medium">{r.titulo}</span>
                      <span className="text-gray-400"> — {r.texto}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1 border-gray-700 text-gray-400">
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}
                className="flex-1 bg-gold hover:bg-gold/80 text-black font-medium" data-testid="btn-save-plan">
                {saving ? 'Guardando...' : (
                  form.id ? 'Actualizar'
                  : selectedIds.length > 1 ? `Asignar a ${selectedIds.length} miembros`
                  : selectedIds.length === 1 ? 'Asignar plan'
                  : 'Crear Plan'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Parse Dialog */}
      <Dialog open={showParseDialog} onOpenChange={setShowParseDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle><Sparkles className="h-5 w-5 inline mr-2 text-gold" />Importar Plan con IA</DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 text-xs">Pega el texto del plan de entrenamiento y la IA lo convertirá en un plan estructurado automáticamente.</p>
          <textarea value={parseText} onChange={e => setParseText(e.target.value)}
            placeholder="Pega aquí el plan completo (semanas, días, ejercicios, recomendaciones...)"
            className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:border-gold"
            data-testid="textarea-parse" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowParseDialog(false)} className="flex-1 border-gray-700 text-gray-400">
              Cancelar
            </Button>
            <Button onClick={handleParse} disabled={parsing || !parseText.trim()}
              className="flex-1 bg-gold hover:bg-gold/80 text-black font-medium" data-testid="btn-parse-go">
              {parsing ? 'Analizando con IA...' : 'Analizar Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle>{showDetail.nombre}</DialogTitle>
              </DialogHeader>
              <p className="text-gray-400 text-sm">{showDetail.descripcion}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                <span><Calendar className="h-3 w-3 inline mr-1" />{showDetail.num_semanas} semanas</span>
                <span><Users className="h-3 w-3 inline mr-1" />{showDetail.cliente_nombre}</span>
                <span>Inicio: {showDetail.fecha_inicio}</span>
              </div>

              {showDetail.evento?.nombre && (
                <div className="bg-gold/10 border border-gold/30 rounded-lg p-3 mt-3">
                  <p className="text-gold font-bold text-sm"><Star className="h-4 w-4 inline mr-1" />{showDetail.evento.nombre}</p>
                  <p className="text-gray-400 text-xs">{showDetail.evento.fecha_inicio} → {showDetail.evento.fecha_fin}</p>
                </div>
              )}

              {/* Progress stats */}
              {planStats[showDetail.id] && planStats[showDetail.id].total_checkins > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-3" data-testid="plan-detail-stats">
                  <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-3 text-center">
                    <CheckCircle className="h-5 w-5 text-green-400 mx-auto mb-1" />
                    <p className="text-green-400 text-xl font-black tabular-nums">{planStats[showDetail.id].total_checkins}</p>
                    <p className="text-gray-500 text-[10px] uppercase">Entrenos hechos</p>
                  </div>
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-3 text-center">
                    <Footprints className="h-5 w-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-blue-400 text-xl font-black tabular-nums">{planStats[showDetail.id].total_km}</p>
                    <p className="text-gray-500 text-[10px] uppercase">Km recorridos</p>
                  </div>
                  {Object.keys(planStats[showDetail.id].by_type || {}).length > 0 && (
                    <div className="col-span-2 bg-gray-800/60 border border-gray-700 rounded-xl p-3">
                      <p className="text-gray-400 text-[10px] font-bold uppercase mb-2">Desglose por tipo</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(planStats[showDetail.id].by_type).sort((a, b) => b[1] - a[1]).map(([tipo, count]) => {
                          const tc = TIPO_COLORS[tipo] || TIPO_COLORS.especial;
                          return (
                            <span key={tipo} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${tc.bg} ${tc.text}`}>
                              {tc.label} {count}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Weeks detail */}
              <div className="space-y-2 mt-3">
                {showDetail.semanas?.map((sem, si) => (
                  <div key={si} className="bg-gray-800 rounded-lg p-3">
                    <h4 className="text-white text-sm font-bold mb-2">{sem.nombre || `Semana ${sem.numero}`}</h4>
                    <div className="space-y-1.5">
                      {sem.dias?.map((dia, di) => (
                        <div key={di} className="flex items-start gap-2 text-xs">
                          <span className="text-gray-500 w-8 flex-shrink-0 font-mono font-bold">{DIAS[dia.dia]}</span>
                          <div className="flex flex-wrap gap-1">
                            {dia.actividades?.map((act, ai) => {
                              const tc = TIPO_COLORS[act.tipo] || TIPO_COLORS.especial;
                              return (
                                <span key={ai} className={`${tc.bg} ${tc.text} px-2 py-0.5 rounded text-[10px]`}>
                                  {tc.label} {act.descripcion}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Recomendaciones */}
              {showDetail.recomendaciones?.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-white text-sm font-bold mb-2">Recomendaciones</h4>
                  <div className="space-y-2">
                    {showDetail.recomendaciones.map((r, i) => (
                      <div key={i} className="bg-gray-800 rounded-lg p-2">
                        <p className="text-gold text-xs font-bold">{r.titulo}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{r.texto}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => openEdit(showDetail)} className="flex-1 border-gray-700 text-gray-400">
                  <Edit className="h-4 w-4 mr-1" /> Editar
                </Button>
                <Button variant="outline" onClick={() => handleDelete(showDetail.id)}
                  className="flex-1 border-red-800 text-red-400 hover:bg-red-900/30">
                  <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}

/**
 * Agrupa planes por (nombre + fecha_inicio + num_semanas + entrenador_id)
 * y muestra una sola card por grupo con el progreso de cada miembro.
 */
function PlanesAgrupados({ planes, planStats, asignables, onOpenDetail, onReload }) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [addModal, setAddModal] = useState(null); // { planRefId, groupName, existingIds }
  const [addSelected, setAddSelected] = useState([]);
  const [addSearch, setAddSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Agrupar
  const groups = {};
  for (const p of planes) {
    const key = `${p.nombre}|${p.fecha_inicio}|${p.num_semanas}|${p.entrenador_id || ''}`;
    if (!groups[key]) groups[key] = { sample: p, items: [] };
    groups[key].items.push(p);
  }
  const sortedGroups = Object.values(groups).sort((a, b) => {
    return (b.sample.fecha_inicio || '').localeCompare(a.sample.fecha_inicio || '');
  });

  const toggle = (key) => setExpandedGroups(s => ({ ...s, [key]: !s[key] }));

  return (
    <div className="space-y-3" data-testid="planes-agrupados">
      {sortedGroups.map((group, gi) => {
        const { sample, items } = group;
        const isGroup = items.length > 1;
        const key = `${sample.nombre}|${sample.fecha_inicio}|${sample.num_semanas}`;
        const expanded = !!expandedGroups[key];

        // Stats agregadas
        let totalCheckins = 0;
        let totalKm = 0;
        let totalEsperados = 0;
        for (const it of items) {
          const s = planStats[it.id];
          if (s) {
            totalCheckins += s.total_checkins || 0;
            totalKm += s.total_km || 0;
            // Estimacion: num_semanas * 3 sesiones/semana como referencia
            totalEsperados += (it.num_semanas || 1) * 3;
          }
        }
        const pct = totalEsperados > 0 ? Math.min(100, Math.round((totalCheckins / totalEsperados) * 100)) : 0;

        // Solo plan único → comportamiento clásico (botón directo)
        if (!isGroup) {
          const plan = sample;
          const stats = planStats[plan.id];
          return (
            <button key={plan.id} onClick={() => onOpenDetail(plan)}
              className="w-full text-left bg-gray-900 rounded-xl border border-gray-800 p-4 hover:border-gray-700 transition-colors"
              data-testid={`plan-${plan.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold truncate">{plan.nombre}</h3>
                  <p className="text-gray-400 text-xs mt-0.5">{plan.descripcion}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {plan.num_semanas} semanas</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {plan.cliente_nombre}</span>
                    <span>Inicio: {plan.fecha_inicio}</span>
                  </div>
                  {stats && stats.total_checkins > 0 && (
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="flex items-center gap-1 text-xs text-green-400 font-medium">
                        <CheckCircle className="h-3 w-3" /> {stats.total_checkins} entrenos
                      </span>
                      {stats.total_km > 0 && (
                        <span className="flex items-center gap-1 text-xs text-blue-400 font-medium">
                          <Footprints className="h-3 w-3" /> {stats.total_km} km
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {plan.evento?.nombre && (
                  <span className="flex-shrink-0 ml-2 px-2 py-1 bg-gold/20 text-gold text-[10px] font-bold rounded-md">
                    <Star className="h-3 w-3 inline mr-0.5" />{plan.evento.nombre}
                  </span>
                )}
              </div>
            </button>
          );
        }

        // Grupo (>=2 planes con mismo nombre/fecha)
        return (
          <div key={gi} className="bg-gray-900 rounded-xl border border-gold/30 overflow-hidden" data-testid={`plan-group-${gi}`}>
            {/* Cabecera */}
            <button
              onClick={() => toggle(key)}
              className="w-full text-left p-4 hover:bg-gray-800/40 transition-colors"
              data-testid={`plan-group-toggle-${gi}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-semibold truncate">{sample.nombre}</h3>
                    <span className="text-[10px] bg-gold/20 text-gold px-2 py-0.5 rounded-full font-bold">
                      GRUPAL · {items.length} miembros
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs mt-0.5">{sample.descripcion}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {sample.num_semanas} semanas</span>
                    <span>Inicio: {sample.fecha_inicio}</span>
                  </div>
                  {/* Stats agregadas + barra */}
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <span className="flex items-center gap-1 text-xs text-green-400 font-medium">
                      <CheckCircle className="h-3 w-3" /> {totalCheckins} entrenos · {items.length} miembros
                    </span>
                    {totalKm > 0 && (
                      <span className="flex items-center gap-1 text-xs text-blue-400 font-medium">
                        <Footprints className="h-3 w-3" /> {totalKm.toFixed(1)} km totales
                      </span>
                    )}
                    {totalEsperados > 0 && (
                      <span className="text-[11px] text-gold">{pct}% promedio</span>
                    )}
                  </div>
                  {totalEsperados > 0 && (
                    <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden mt-2">
                      <div
                        className="h-full bg-gradient-to-r from-gold to-yellow-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 ml-2 flex items-center gap-1">
                  {sample.evento?.nombre && (
                    <span className="px-2 py-1 bg-gold/20 text-gold text-[10px] font-bold rounded-md">
                      <Star className="h-3 w-3 inline mr-0.5" />{sample.evento.nombre}
                    </span>
                  )}
                  {expanded ? <ChevronUp className="h-4 w-4 text-gold" /> : <ChevronDown className="h-4 w-4 text-gold" />}
                </div>
              </div>
            </button>

            {/* Listado de miembros */}
            {expanded && (
              <>
                <div className="border-t border-gray-800 bg-gray-950/50 divide-y divide-gray-800/60" data-testid={`plan-group-members-${gi}`}>
                  {[...items]
                    .sort((a, b) => (b.cliente_nombre || '').localeCompare(a.cliente_nombre || '') ? 1 : -1)
                    .map(plan => {
                      const stats = planStats[plan.id] || {};
                      const sCheckins = stats.total_checkins || 0;
                      const sKm = stats.total_km || 0;
                      const esperados = (plan.num_semanas || 1) * 3;
                      const memberPct = esperados > 0 ? Math.min(100, Math.round((sCheckins / esperados) * 100)) : 0;
                      return (
                        <button
                          key={plan.id}
                          onClick={() => onOpenDetail(plan)}
                          className="w-full text-left p-3 hover:bg-gray-800/40 transition-colors flex items-center gap-3"
                          data-testid={`plan-member-${plan.id}`}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold/40 to-gold/10 text-gold flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {(plan.cliente_nombre || '?')[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm font-medium truncate">{plan.cliente_nombre || 'Sin asignar'}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                                <div
                                  className={`h-full ${memberPct >= 75 ? 'bg-green-500' : memberPct >= 40 ? 'bg-yellow-500' : memberPct > 0 ? 'bg-orange-500' : 'bg-gray-700'}`}
                                  style={{ width: `${memberPct}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-gray-400 w-10 text-right">{memberPct}%</span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs font-semibold text-green-400 flex items-center gap-0.5 justify-end">
                              <CheckCircle className="h-3 w-3" /> {sCheckins}
                            </div>
                            {sKm > 0 && (
                              <div className="text-[10px] text-blue-400">{sKm} km</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                </div>
                {/* Boton añadir miembros */}
                <div className="border-t border-gray-800 bg-gray-950/60 p-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const existingIds = items.map(i => i.cliente_id);
                      setAddModal({ planRefId: sample.id, groupName: sample.nombre, existingIds });
                      setAddSelected([]);
                      setAddSearch('');
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-md bg-gold/15 hover:bg-gold/25 text-gold text-sm font-semibold transition-colors"
                    data-testid={`btn-add-members-${gi}`}
                  >
                    <Plus className="h-4 w-4" /> Añadir miembros al grupo
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}

      {/* Modal añadir miembros */}
      {addModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setAddModal(null); }}
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          data-testid="add-members-modal"
        >
          <div className="bg-gray-900 rounded-xl border border-gold/30 w-full max-w-md max-h-[90vh] overflow-y-auto p-5">
            <h3 className="text-gold text-lg font-bold mb-1">Añadir miembros al grupo</h3>
            <p className="text-gray-400 text-xs mb-3">Plan: <span className="text-white">{addModal.groupName}</span></p>
            
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 space-y-2">
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                  <Input
                    placeholder="Buscar miembro..."
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white text-sm pl-7 h-8"
                  />
                </div>
                {addSelected.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setAddSelected([])}
                    className="text-[11px] px-2 py-1 rounded bg-red-900/30 hover:bg-red-900/50 text-red-300"
                  >Limpiar</button>
                )}
              </div>
              
              {addSelected.length > 0 && (
                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto p-1 bg-gray-900/50 rounded">
                  {addSelected.map(id => {
                    const u = (asignables || []).find(x => x.id === id);
                    if (!u) return null;
                    return (
                      <span key={id} className="inline-flex items-center gap-1 bg-gold/20 text-gold text-[10px] px-2 py-0.5 rounded-full">
                        {u.nombre}
                        <button type="button" onClick={() => setAddSelected(addSelected.filter(s => s !== id))} className="hover:text-white">×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              
              <div className="max-h-72 overflow-y-auto space-y-0.5">
                {(asignables || [])
                  .filter(u => !addModal.existingIds.includes(u.id))
                  .filter(u => !addSearch || (u.nombre || '').toLowerCase().includes(addSearch.toLowerCase()) || (u.email || '').toLowerCase().includes(addSearch.toLowerCase()))
                  .map(u => {
                    const sel = addSelected.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setAddSelected(sel ? addSelected.filter(s => s !== u.id) : [...addSelected, u.id])}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${sel ? 'bg-gold/20 text-gold' : 'text-gray-300 hover:bg-gray-700'}`}
                        data-testid={`add-pick-${u.id}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${sel ? 'bg-gold border-gold' : 'border-gray-600'}`}>
                          {sel && <Check className="h-3 w-3 text-gray-900" />}
                        </div>
                        <span className="flex-1 truncate">{u.nombre}</span>
                        {u.role === 'entrenador' && <span className="text-[9px] bg-gray-700 px-1 rounded">ENTRENADOR</span>}
                        {u.role === 'client_plus' && <span className="text-[9px] bg-blue-700 px-1 rounded">PLUS</span>}
                        {u.es_pro && <span className="text-[9px] bg-amber-600 text-black px-1 rounded">PRO</span>}
                      </button>
                    );
                  })}
                {((asignables || []).filter(u => !addModal.existingIds.includes(u.id)).length === 0) && (
                  <p className="text-gray-500 text-xs text-center py-3">Todos los miembros ya están en el grupo.</p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" onClick={() => setAddModal(null)} disabled={adding} className="border-gray-700 text-gray-300 hover:bg-gray-800">
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (addSelected.length === 0) return;
                  setAdding(true);
                  try {
                    const { data } = await axios.post(`${API}/planes/grupo/agregar-miembros`, {
                      plan_ref_id: addModal.planRefId,
                      cliente_ids: addSelected,
                      notificar: true,
                      feed_publico: false,
                    }, { headers });
                    alert(`✅ ${data.agregados} miembro(s) añadido(s): ${(data.destinatarios || []).join(', ')}`);
                    setAddModal(null);
                    if (onReload) await onReload();
                  } catch (e) {
                    alert('Error: ' + (e.response?.data?.detail || e.message));
                  } finally { setAdding(false); }
                }}
                disabled={adding || addSelected.length === 0}
                className="bg-gold hover:bg-gold/80 text-black font-medium"
                data-testid="btn-confirm-add-members"
              >
                {adding ? 'Añadiendo...' : `Añadir ${addSelected.length || ''}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

